import type { InvoiceData, InvoiceSettings, InvoiceItem } from './types';

export const DEFAULT_SETTINGS: InvoiceSettings = {
  currency: 'USD',
  currencySymbol: '$',
  taxLabel: 'Tax',
  themeColor: '#0ea5e9', // Brand 500 (Sky Blue)
  template: 'modern',
  language: 'en',
  autoSave: true,
};

/**
 * Calculate invoice totals from line items
 */
export const calculateInvoiceTotals = (items: InvoiceItem[]) => {
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.rate, 0);
  const taxAmount = items.reduce(
    (sum, item) => sum + (item.quantity * item.rate * item.tax) / 100,
    0
  );
  const total = subtotal + taxAmount;
  return { subtotal, taxAmount, total };
};

export const DUMMY_INVOICE: InvoiceData = {
  id: 'pro-invoice-9921',
  number: 'INV-2026-0001',
  date: new Date().toISOString().split('T')[0],
  dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  senderName: 'Skyline Digital Solutions',
  senderAddress: '202 Azure Terrace, Level 4\nSan Francisco, CA 94103',
  senderEmail: 'billing@skylinedigital.io',
  senderPhone: '+1 (415) 555-0198',
  senderTaxId: 'EIN-88-293847',
  recipientName: 'Alex Rivera',
  recipientCompany: 'Pioneer Ventures Group',
  recipientAddress: '101 Innovation Blvd\nAustin, TX 78701',
  recipientEmail: 'alex@pioneer.vc',
  items: [
    {
      id: '1',
      description: 'Enterprise UI/UX Design System Development',
      quantity: 1,
      rate: 3500,
      tax: 0,
    },
    {
      id: '2',
      description: 'React Frontend Implementation (Sprint 3 & 4)',
      quantity: 40,
      rate: 125,
      tax: 0,
    },
    {
      id: '3',
      description: 'API Integration & Middleware Orchestration',
      quantity: 15,
      rate: 140,
      tax: 0,
    },
  ],
  notes:
    'This invoice covers development work performed between January 1st and January 15th, 2026.',
  terms: 'Payment is due within 14 days of receipt. 5% late fee applies after 30 days.',
  paymentInfo:
    'Bank: Silicon Valley Bank\nAccount Name: Skyline Digital LLC\nAccount: **** 2938\nRouting: 121000358',
  logo: null,
  status: 'Draft',
};

/**
 * Sample invoices for demo/testing purposes
 */
export const SAMPLE_INVOICES: InvoiceData[] = [
  // Invoice 1 - Tech Consulting
  {
    id: 'sample-inv-001',
    number: 'INV-2026-001',
    date: '2026-01-15',
    dueDate: '2026-01-29',
    senderName: 'TechFlow Consulting',
    senderAddress: '500 Market Street, Suite 300\nSan Francisco, CA 94105',
    senderEmail: 'invoices@techflow.io',
    senderPhone: '+1 (415) 555-1234',
    senderTaxId: 'EIN-12-3456789',
    recipientName: 'Sarah Johnson',
    recipientCompany: 'Acme Corp',
    recipientAddress: '1000 Corporate Blvd\nNew York, NY 10001',
    recipientEmail: 'sarah.johnson@acme.com',
    items: [
      {
        id: '1',
        description: 'Cloud Architecture Consultation (8 hours)',
        quantity: 8,
        rate: 250,
        tax: 0,
      },
      { id: '2', description: 'AWS Migration Planning', quantity: 1, rate: 5000, tax: 0 },
      { id: '3', description: 'Security Audit & Documentation', quantity: 1, rate: 3500, tax: 8.5 },
    ],
    notes: 'Thank you for choosing TechFlow Consulting!',
    terms: 'Net 14 days. Late payments subject to 2% monthly interest.',
    paymentInfo: 'Wire Transfer:\nBank: Chase\nAccount: ****4567\nRouting: 021000021',
    logo: null,
    status: 'Sent',
  },
  // Invoice 2 - Freelance Design
  {
    id: 'sample-inv-002',
    number: 'INV-2026-002',
    date: '2026-01-10',
    dueDate: '2026-02-10',
    senderName: 'Creative Studio by Maya',
    senderAddress: '789 Design District\nAustin, TX 78701',
    senderEmail: 'hello@mayacreative.co',
    senderPhone: '+1 (512) 555-9876',
    recipientName: 'Michael Chen',
    recipientCompany: 'StartupXYZ',
    recipientAddress: '456 Innovation Way\nSeattle, WA 98101',
    recipientEmail: 'michael@startupxyz.com',
    items: [
      { id: '1', description: 'Brand Identity Package', quantity: 1, rate: 4500, tax: 0 },
      { id: '2', description: 'Logo Design (3 concepts)', quantity: 1, rate: 1500, tax: 0 },
      { id: '3', description: 'Business Card Design', quantity: 1, rate: 350, tax: 0 },
      { id: '4', description: 'Social Media Kit (5 templates)', quantity: 5, rate: 200, tax: 0 },
    ],
    notes: 'All source files will be delivered upon payment.',
    terms: 'Payment due within 30 days. 50% deposit required for new clients.',
    paymentInfo: 'PayPal: maya@creativestudio.co\nVenmo: @MayaCreative',
    logo: null,
    status: 'Paid',
  },
  // Invoice 3 - Web Development
  {
    id: 'sample-inv-003',
    number: 'INV-2026-003',
    date: '2026-01-20',
    dueDate: '2026-02-03',
    senderName: 'DevOps Pro LLC',
    senderAddress: '123 Code Street\nDenver, CO 80202',
    senderEmail: 'billing@devopspro.com',
    senderPhone: '+1 (303) 555-4321',
    senderTaxId: 'EIN-98-7654321',
    recipientName: 'Emily Watson',
    recipientCompany: 'E-Commerce Plus',
    recipientAddress: '200 Retail Lane\nChicago, IL 60601',
    recipientEmail: 'emily@ecommerceplus.com',
    items: [
      { id: '1', description: 'E-commerce Platform Development', quantity: 1, rate: 15000, tax: 0 },
      {
        id: '2',
        description: 'Payment Gateway Integration (Stripe + PayPal)',
        quantity: 1,
        rate: 2500,
        tax: 0,
      },
      { id: '3', description: 'Inventory Management System', quantity: 1, rate: 4000, tax: 0 },
      {
        id: '4',
        description: 'Monthly Maintenance (3 months prepaid)',
        quantity: 3,
        rate: 500,
        tax: 6.25,
      },
    ],
    notes: 'Milestone 2 of 3 - Backend development complete.',
    terms: 'Net 14. Project-based invoicing per agreed milestones.',
    paymentInfo: 'ACH Transfer:\nBank: Wells Fargo\nAccount: ****8901\nRouting: 102000076',
    logo: null,
    status: 'Draft',
  },
  // Invoice 4 - Marketing Services
  {
    id: 'sample-inv-004',
    number: 'INV-2026-004',
    date: '2026-01-22',
    dueDate: '2026-02-05',
    senderName: 'Digital Marketing Agency',
    senderAddress: '555 Ad Avenue\nLos Angeles, CA 90001',
    senderEmail: 'accounts@dmagency.com',
    senderPhone: '+1 (213) 555-7890',
    recipientName: 'David Park',
    recipientCompany: 'Fitness First Studios',
    recipientAddress: '888 Wellness Way\nMiami, FL 33101',
    recipientEmail: 'david@fitnessfirst.com',
    items: [
      { id: '1', description: 'Google Ads Management (January)', quantity: 1, rate: 2000, tax: 0 },
      { id: '2', description: 'Facebook/Instagram Ads (January)', quantity: 1, rate: 1500, tax: 0 },
      { id: '3', description: 'Ad Spend Reimbursement', quantity: 1, rate: 5000, tax: 0 },
      { id: '4', description: 'Monthly Analytics Report', quantity: 1, rate: 500, tax: 0 },
      { id: '5', description: 'A/B Testing Setup (3 campaigns)', quantity: 3, rate: 300, tax: 0 },
    ],
    notes: 'Campaign performance: 23% increase in conversions MoM.',
    terms: 'Due upon receipt. Ad spend billed at cost.',
    paymentInfo: 'Credit Card accepted\nInvoice Link: pay.dmagency.com/inv-004',
    logo: null,
    status: 'Sent',
  },
];

export const EMPTY_INVOICE: InvoiceData = {
  id: '',
  number: `INV-${new Date().getFullYear()}-001`,
  date: new Date().toISOString().split('T')[0],
  dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  senderName: '',
  senderAddress: '',
  senderEmail: '',
  senderPhone: '',
  senderTaxId: '',
  recipientName: '',
  recipientCompany: '',
  recipientAddress: '',
  items: [{ id: '1', description: 'Service Description', quantity: 1, rate: 0, tax: 0 }],
  notes: '',
  terms: 'Payment is due within 14 days.',
  paymentInfo: 'Bank Name:\nAccount Number:',
  logo: null,
  status: 'Draft',
};

/** Map currency codes to symbols */
export const CURRENCY_MAP: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '¥',
  BRL: 'R$',
  PKR: '₨',
  AED: 'د.إ',
};

export const TRANSLATIONS: Record<string, Record<string, string>> = {
  en: {
    invoice: 'Invoice',
    date: 'Date',
    dueDate: 'Due Date',
    billTo: 'Bill To',
    from: 'From',
    description: 'Description',
    qty: 'Qty',
    rate: 'Rate',
    amount: 'Amount',
    subtotal: 'Subtotal',
    total: 'Total',
    paymentInfo: 'Payment Info',
    notes: 'Notes',
  },
  es: {
    invoice: 'Factura',
    date: 'Fecha',
    dueDate: 'Fecha de Vencimiento',
    billTo: 'Facturar A',
    from: 'De',
    description: 'Descripción',
    qty: 'Cant.',
    rate: 'Tarifa',
    amount: 'Monto',
    subtotal: 'Subtotal',
    total: 'Total',
    paymentInfo: 'Información de Pago',
    notes: 'Notas',
  },
  fr: {
    invoice: 'Facture',
    date: 'Date',
    dueDate: 'Date d\'Échéance',
    billTo: 'Facturer À',
    from: 'De',
    description: 'Description',
    qty: 'Qté',
    rate: 'Taux',
    amount: 'Montant',
    subtotal: 'Sous-total',
    total: 'Total',
    paymentInfo: 'Info de Paiement',
    notes: 'Notes',
  },
  de: {
    invoice: 'Rechnung',
    date: 'Datum',
    dueDate: 'Fälligkeitsdatum',
    billTo: 'Rechnungsempfänger',
    from: 'Von',
    description: 'Beschreibung',
    qty: 'Menge',
    rate: 'Satz',
    amount: 'Betrag',
    subtotal: 'Zwischensumme',
    total: 'Gesamt',
    paymentInfo: 'Zahlungsinfo',
    notes: 'Anmerkungen',
  },
  ur: {
    invoice: 'انوائس',
    date: 'تاریخ',
    dueDate: 'آخری تاریخ',
    billTo: 'بل وصول کنندہ',
    from: 'سے',
    description: 'تفصیل',
    qty: 'مقدار',
    rate: 'شرح',
    amount: 'رقم',
    subtotal: 'ذیلی کل',
    total: 'کل',
    paymentInfo: 'ادائیگی کی معلومات',
    notes: 'نوٹس',
  },
};
